import axios from "axios";
import i18next from "i18next";
import {
    useEffect,
    useState
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';
import { setCategory, setskills } from "../app/features/userSlice";
import { useDispatch, useSelector } from "react-redux";

export const useGetSkillsHook = () => {
    const { t } = useTranslation();
    const currentLanguage = i18next.language;
    const [skills, setSkills] = useState([]);
    const dispatch = useDispatch(null)

    useEffect(() => {
        const fetchSkills = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}job/getJobsSkill`);
                const data = response.data;
                dispatch(setskills([...response.data]));

                setSkills(data.map(skill => ({
                    value: currentLanguage === "en" ? skill.labelen : skill.labelar,
                    label: currentLanguage === "en" ? skill.labelen : skill.labelar,
                    id: skill.value
                })));
            } catch (error) {
                console.error('Error fetching skills:', error);
            }
        };

        fetchSkills();
    }, [t, currentLanguage]);
    return skills;
};


export const useGetCompanyIndustriesHook = () => {
    const { t } = useTranslation();

    const industry_codes = ["agriculture", "automotive", "banking", "biotechnology", "chemicals", "construction", "consulting", "consumer_goods", "education", "energy", "engineering", "entertainment", "financial_services", "food_beverage", "healthcare", "hospitality", "insurance", "internet", "logistics", "manufacturing", "marketing", "media", "non_profit", "pharmaceutical", "real_estate", "retail", "software", "telecommunications", "transportation", "utilities"];
    const industries = [];

    industry_codes.forEach(industry => {
        industries.push({
            value: industry,
            label: t(`general.company_industries.${industry}`)
        });
    });

    return industries;
};

export const useGetJobCategoriesHook = () => {
    const [jobCategories, setJobCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const dispatch = useDispatch(null)
    const { t } = useTranslation();
    const currentLanguage = i18next.language;
    const user = useSelector(state => state.user.user);

    useEffect(() => {
        const fetchJobCategories = async () => {
            setLoading(true);
            try {
                const accountType = user?.employer_status || user?.jobseeker_status;
                // Fetch categories whether user is logged in or not
                const response = await axios.get(`${process.env.REACT_APP_API_URL}job/getJobsCategory`, {
                    params: { accountType }
                });
                const data = await response.data;
                
                if (data && data.length > 0) {
                    dispatch(setCategory([...data]));
                    
                    const formattedCategories = data.map(category => ({
                        value: category.value,
                        label: currentLanguage === "en" ? category.labelen : category.labelar,
                        id: category.value,
                        // Store original fields for debugging
                        labelen: category.labelen,
                        labelar: category.labelar
                    }));
                    setJobCategories(formattedCategories);
                } else {
                    // Provide default categories if API returns empty
                    setJobCategories([
                        { value: "1", label: "Information Technology", id: "1", labelen: "Information Technology", labelar: "تكنولوجيا المعلومات" },
                        { value: "2", label: "Marketing", id: "2", labelen: "Marketing", labelar: "تسويق" },
                        { value: "3", label: "Finance", id: "3", labelen: "Finance", labelar: "مالية" },
                        { value: "4", label: "Healthcare", id: "4", labelen: "Healthcare", labelar: "الرعاية الصحية" },
                        { value: "5", label: "Education", id: "5", labelen: "Education", labelar: "التعليم" }
                    ]);
                }
            } catch (error) {
                console.error('Error fetching job categories:', error);
                // Provide default categories if API fails
                setJobCategories([
                    { value: "1", label: "Information Technology", id: "1", labelen: "Information Technology", labelar: "تكنولوجيا المعلومات" },
                    { value: "2", label: "Marketing", id: "2", labelen: "Marketing", labelar: "تسويق" },
                    { value: "3", label: "Finance", id: "3", labelen: "Finance", labelar: "مالية" },
                    { value: "4", label: "Healthcare", id: "4", labelen: "Healthcare", labelar: "الرعاية الصحية" },
                    { value: "5", label: "Education", id: "5", labelen: "Education", labelar: "التعليم" }
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchJobCategories();
    }, [currentLanguage]);

    return jobCategories;
};


export const useGetEducationLevelsHook = () => {
    const { t } = useTranslation();

    const education_levels_codes = [
        "no_formal_education",
        "primary_education",
        "lower_secondary_education",
        "upper_secondary_education",
        "vocational_education_and_training",
        "associate_degree",
        "bachelors_degree",
        "masters_degree",
        "doctoral_degree",
        "postdoctoral_research",
        "professional_certification"
    ];
    const education_levels = [];

    education_levels_codes.forEach(education_level => {
        education_levels.push({
            value: education_level,
            label: t(`general.education_levels.${education_level}`)
        });
    });

    return education_levels;
};

export const useGetCurrenciesHook = (includeCurrencies = []) => {

    const currencies_list = [
        { code: "usd", name: "USD", symbol: "$" },
        { code: "eur", name: "EUR", symbol: "€" },
        { code: "gbp", name: "GBP", symbol: "£" },
        { code: "jpy", name: "JPY", symbol: "¥" },
        { code: "cny", name: "CNY", symbol: "¥" },
        { code: "cad", name: "CAD", symbol: "$" },
        { code: "aud", name: "AUD", symbol: "$" },
        { code: "chf", name: "CHF", symbol: "CHF" },
        { code: "sek", name: "SEK", symbol: "kr" },
        { code: "nok", name: "NOK", symbol: "kr" },
        { code: "dkk", name: "DKK", symbol: "kr" },
        { code: "rub", name: "RUB", symbol: "₽" },
        { code: "inr", name: "INR", symbol: "₹" },
        { code: "brl", name: "BRL", symbol: "R$" },
        { code: "zar", name: "ZAR", symbol: "R" },
        { code: "hkd", name: "HKD", symbol: "$" },
        { code: "sgd", name: "SGD", symbol: "$" },
        { code: "nzd", name: "NZD", symbol: "$" },
        { code: "thb", name: "THB", symbol: "฿" },
        { code: "php", name: "PHP", symbol: "₱" },
        { code: "idr", name: "IDR", symbol: "Rp" },
        { code: "myr", name: "MYR", symbol: "RM" },
        { code: "vnd", name: "VND", symbol: "₫" },
        { code: "krw", name: "KRW", symbol: "₩" },
        { code: "try", name: "TRY", symbol: "₺" },
        { code: "mxn", name: "MXN", symbol: "$" },
        { code: "ars", name: "ARS", symbol: "$" },
        { code: "cop", name: "COP", symbol: "$" },
        { code: "clp", name: "CLP", symbol: "$" },
        { code: "pen", name: "PEN", symbol: "S/." },
        { code: "aed", name: "AED", symbol: "د.إ" },
        { code: "sar", name: "SAR", symbol: "ر.س" },
        { code: "qar", name: "QAR", symbol: "ر.ق" },
        { code: "omr", name: "OMR", symbol: "ر.ع." },
        { code: "kwd", name: "KWD", symbol: "د.ك" },
        { code: "bhd", name: "BHD", symbol: "د.ب" },
        { code: "jod", name: "JOD", symbol: "د.ا" },
        { code: "ils", name: "ILS", symbol: "₪" },
        { code: "pln", name: "PLN", symbol: "zł" },
        { code: "czk", name: "CZK", symbol: "Kč" },
        { code: "huf", name: "HUF", symbol: "Ft" },
        { code: "ron", name: "RON", symbol: "lei" },
        { code: "sek", name: "SEK", symbol: "kr" },
        { code: "nok", name: "NOK", symbol: "kr" },
        { code: "dkk", name: "DKK", symbol: "kr" },
        { code: "rub", name: "RUB", symbol: "₽" },
        { code: "inr", name: "INR", symbol: "₹" },
        { code: "brl", name: "BRL", symbol: "R$" },
        { code: "zar", name: "ZAR", symbol: "R" },
        { code: "hkd", name: "HKD", symbol: "$" },
        { code: "sgd", name: "SGD", symbol: "$" },
        { code: "nzd", name: "NZD", symbol: "$" },
        { code: "thb", name: "THB", symbol: "฿" },
        { code: "php", name: "PHP", symbol: "₱" },
        { code: "idr", name: "IDR", symbol: "Rp" },
        { code: "myr", name: "MYR", symbol: "RM" },
        { code: "vnd", name: "VND", symbol: "₫" },
        { code: "krw", name: "KRW", symbol: "₩" },
        { code: "try", name: "TRY", symbol: "₺" },
        { code: "mxn", name: "MXN", symbol: "$" },
        { code: "ars", name: "ARS", symbol: "$" },
        { code: "cop", name: "COP", symbol: "$" },
        { code: "clp", name: "CLP", symbol: "$" },
        { code: "pen", name: "PEN", symbol: "S/." },
        { code: "aed", name: "AED", symbol: "د.إ" },
        { code: "sar", name: "SAR", symbol: "ر.س" },
        { code: "qar", name: "QAR", symbol: "ر.ق" },
        { code: "omr", name: "OMR", symbol: "ر.ع." },
        { code: "kwd", name: "KWD", symbol: "د.ك" },
        { code: "bhd", name: "BHD", symbol: "د.ب" },
        { code: "jod", name: "JOD", symbol: "د.ا" },
        { code: "ils", name: "ILS", symbol: "₪" },
        { code: "pln", name: "PLN", symbol: "zł" },
        { code: "czk", name: "CZK", symbol: "Kč" },
        { code: "huf", name: "HUF", symbol: "Ft" }
    ];
    const currencies = [];

    currencies_list.forEach(currency => {

        if (includeCurrencies.length > 0) {

            if (includeCurrencies.includes(currency.code)) {
                currencies.push({
                    value: currency.code,
                    label: currency.name + " (" + currency.symbol + ")"
                });
            }

        }
        else {
            currencies.push({
                value: currency.code,
                label: currency.name + " (" + currency.symbol + ")"
            });
        }

    });

    const uniqueCurrencyValues = new Set();
    const uniqueCurrencies = currencies.filter(currency => {
        if (uniqueCurrencyValues.has(currency.value)) {
            return false;
        } else {
            uniqueCurrencyValues.add(currency.value);
            return true;
        }
    });

    return uniqueCurrencies;

};

export const useGetQueryParam = (param) => {
    const location = useLocation();

    const urlParams = new URLSearchParams(location.search);
    return urlParams.get(param);
};

export const useDebounce = (func, wait) => {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
}

export const useGetCities = (includeCountry = []) => {
    const { t } = useTranslation();

    const city_codes_sar = ["riyadh", "jeddah", "mecca", "medina", "dammam", "khobar", "dhahran", "tabuk", "qatif", "al_hasa", "hofuf", "jubail", "abha", "khamis_mushait", "najran", "jizan", "taif", "al_khafji", "yanbu", "al_kharj", "buraidah", "al_jouf", "hail", "arar", "sakaka", "al_mubarraz", "unaizah", "ras_tanura", "rabigh", "al_lith"];
    const city_codes_oman = ["muscat", "salalah", "sohar", "nizwa", "sur", "ibra", "barka", "rustaq", "buraimi", "ibri", "khasab", "matrah", "seeb", "samail", "al_ashkharah", "bidiyah", "badiyah", "bahla", "al_kamil_wal_wafi", "adam", "al_mudhaibi", "al_suwaiq", "al_awabi", "al_buraimi", "al_hamra", "duqm", "dank", "shinas", "liwa", "mahwit", "masirah"];
    const city_codes_kuwait = ["kuwait_city", "hawalli", "salmiya", "farwaniya", "jleeb_al_shuyoukh", "fahaheel", "mangaf", "abu_halifa", "mahboula", "al_jahra", "sabah_al_salem", "sulaibikhat", "raqai", "mishref", "jabriya", "shalim", "al_zour", "wafra", "abdali", "al_ardiyah"];
    const city_codes_bahrein = ["manama", "muharraq", "riffa", "hamad_town", "isa_town", "sitra", "jidhafs", "zallaq", "duraz", "budaiya", "bilad_al_qadeem", "ain_adari", "seef", "saar", "janabiyah", "busaiteen", "jasra", "karzakan", "malikiya", "diraz", "maameer", "buri", "hidd", "al_dar"];
    const city_codes_qatar = ["doha", "al_rayyan", "al_wakrah", "umm_said", "madinat_ash_shamal", "al_khor", "al_shahaniya", "dukhan", "mesaieed", "ras_laffan", "al_wukair", "al_thakhira", "al_ghuwariyah", "al_jumaliyah", "fuwayrit", "al_karanaah", "ash_shihaniyah", "simaismah"];
    const city_codes_uae = ["abu_dhabi", "dubai", "sharjah", "al_ain", "ajman", "ras_al_khaimah", "fujairah", "umm_al_quwain", "khor_fakkan", "dibba_al_fujairah", "kalba", "al_dhaid", "al_madam", "al_jazirah_al_hamra", "masafi", "hili", "al_qua"];

    const cities = [];

    if (includeCountry?.length > 0) {

        if (includeCountry.includes("SA")) {
            city_codes_sar.forEach(city_code => {
                cities.push({
                    value: city_code,
                    label: t(`general.city.${city_code}`)
                });
            });
        }

        if (includeCountry.includes("OM")) {
            city_codes_oman.forEach(city_code => {
                cities.push({
                    value: city_code,
                    label: t(`general.city.${city_code}`)
                });
            });
        }

        if (includeCountry.includes("BH")) {
            city_codes_bahrein.forEach(city_code => {
                cities.push({
                    value: city_code,
                    label: t(`general.city.${city_code}`)
                });
            });
        }

        if (includeCountry.includes("KW")) {
            city_codes_kuwait.forEach(city_code => {
                cities.push({
                    value: city_code,
                    label: t(`general.city.${city_code}`)
                });
            });
        }

        if (includeCountry.includes("AE")) {
            city_codes_uae.forEach(city_code => {
                cities.push({
                    value: city_code,
                    label: t(`general.city.${city_code}`)
                });
            });
        }

        if (includeCountry.includes("QA")) {
            city_codes_qatar.forEach(city_code => {
                cities.push({
                    value: city_code,
                    label: t(`general.city.${city_code}`)
                });
            });
        }

    }
    else {
        city_codes_sar.forEach(city_code => {
            cities.push({
                value: city_code,
                label: t(`general.city.${city_code}`)
            });
        });
        city_codes_oman.forEach(city_code => {
            cities.push({
                value: city_code,
                label: t(`general.city.${city_code}`)
            });
        });
        city_codes_kuwait.forEach(city_code => {
            cities.push({
                value: city_code,
                label: t(`general.city.${city_code}`)
            });
        });
        city_codes_bahrein.forEach(city_code => {
            cities.push({
                value: city_code,
                label: t(`general.city.${city_code}`)
            });
        });
        city_codes_qatar.forEach(city_code => {
            cities.push({
                value: city_code,
                label: t(`general.city.${city_code}`)
            });
        });
        city_codes_uae.forEach(city_code => {
            cities.push({
                value: city_code,
                label: t(`general.city.${city_code}`)
            });
        });
    }

    return cities;
};


export const useGetStates = (includeCountry = []) => {
    const { t } = useTranslation();
    const state_codes_sar = ["riyadh_region", "makkah_region", "madinah_region", "eastern_region", "asir_region", "tabuk_region", "hail_region", "najran_region", "jizan_region", "bahah_region", "northern_borders_region", "qassim_region", "al_jouf_region"];
    const state_codes_oman = ["muscat_governorate", "dhofar_governorate", "al_batinah_north", "al_batinah_south", "ad_dakhiliyah", "ash_sharqiyah_north", "ash_sharqiyah_south", "ad_dhahirah", "al_buraimi", "musandam"];
    const state_codes_kuwait = ["ahmadi", "jahra", "capital", "farwaniya", "hawalli", "mubarak_al_kabeer"];
    const state_codes_bahrein = ["capital_governorate", "muharraq_governorate", "northern_governorate", "southern_governorate"];
    const state_codes_qatar = ["doha_municipality", "al_rayyan_municipality", "al_wakrah_municipality", "umm_said_municipality", "al_khor_municipality"];
    const state_codes_uae = ["abu_dhabi_emirate", "dubai_emirate", "sharjah_emirate", "ajman_emirate", "umm_al_quwain_emirate", "ras_al_khaimah_emirate", "fujairah_emirate"];

    const states = [];

    if (includeCountry?.length > 0) {

        if (includeCountry.includes("SA")) {
            state_codes_sar.forEach(state_code => {
                states.push({
                    value: state_code,
                    label: t(`general.state.${state_code}`)
                });
            });
        }

        if (includeCountry.includes("OM")) {
            state_codes_oman.forEach(state_code => {
                states.push({
                    value: state_code,
                    label: t(`general.state.${state_code}`)
                });
            });
        }

        if (includeCountry.includes("BH")) {
            state_codes_bahrein.forEach(state_code => {
                states.push({
                    value: state_code,
                    label: t(`general.state.${state_code}`)
                });
            });
        }

        if (includeCountry.includes("KW")) {
            state_codes_kuwait.forEach(state_code => {
                states.push({
                    value: state_code,
                    label: t(`general.state.${state_code}`)
                });
            });
        }

        if (includeCountry.includes("AE")) {
            state_codes_uae.forEach(state_code => {
                states.push({
                    value: state_code,
                    label: t(`general.state.${state_code}`)
                });
            });
        }

        if (includeCountry.includes("QA")) {
            state_codes_qatar.forEach(state_code => {
                states.push({
                    value: state_code,
                    label: t(`general.state.${state_code}`)
                });
            });
        }

    } else {
        // Default return of all states if no country is specified
        state_codes_sar.forEach(state_code => {
            states.push({
                value: state_code,
                label: t(`general.state.${state_code}`)
            });
        });
        state_codes_oman.forEach(state_code => {
            states.push({
                value: state_code,
                label: t(`general.state.${state_code}`)
            });
        });
        state_codes_kuwait.forEach(state_code => {
            states.push({
                value: state_code,
                label: t(`general.state.${state_code}`)
            });
        });
        state_codes_bahrein.forEach(state_code => {
            states.push({
                value: state_code,
                label: t(`general.state.${state_code}`)
            });
        });
        state_codes_qatar.forEach(state_code => {
            states.push({
                value: state_code,
                label: t(`general.state.${state_code}`)
            });
        });
        state_codes_uae.forEach(state_code => {
            states.push({
                value: state_code,
                label: t(`general.state.${state_code}`)
            });
        });
    }

    return states;
};






export const useGetCitiesOfState = (includeState = []) => {
    const { t } = useTranslation();

    const state_city_map = {
        // Saudi Arabia
        "riyadh_region": ["riyadh", "al_kharj", "al_ghat", "al_qua", "al_dawadmi"],
        "makkah_region": ["jeddah", "mecca", "taif", "rabigh", "al_lith"],
        "madinah_region": ["medina", "yanbu", "khaybar", "al_hanakia"],
        "eastern_region": ["dammam", "khobar", "dhahran", "jubail", "qatif", "al_hasa", "hofuf", "ras_tanura", "al_khafji"],
        "asir_region": ["abha", "khamis_mushait", "bishah", "nabiya"],
        "najran_region": ["najran"],
        "jizan_region": ["jizan", "sabya", "al_ardah"],
        "hail_region": ["hail"],
        "tabuk_region": ["tabuk", "al_wajh", "umluj"],
        "bahah_region": ["baha", "alqura", "almikhwah"],
        "al_jouf_region": ["sakaka", "al_jouf", "domat_aljandal"],
        "northern_borders_region": ["arar", "rafha", "turaif"],
        "qassim_region": ["unaizah",],


        // Oman
        "muscat_governorate": ["muscat", "bawshar", "seeb", "khoor", "madinat_al_irfan", "qalhat", "muttrah", "rustaq"],
        "dhofar_governorate": ["salalah", "mirbat", "haima", "dhalqut", "thumrait"],
        "al_batinah_north": ["suwadi", "khabourah", "saham", "mussanah"],
        "al_batinah_south": ["barka", "wadi_al_ma'awil", "nakhal"],
        "ad_dakhiliyah": ["nizwa", "bahla", "manah", "izki", "al_hamra"],
        "ash_sharqiyah_north": ["ibra", "al_mudhaibi", "bilad_sayt"],
        "ash_sharqiyah_south": ["sur", "quriyat", "wakan", "fins"],
        "ad_dhahirah": ["bahla", "rustaq", "al_dhahirah"],
        "al_buraimi": ["buraimi", "al_suwayq", "al_yarq"],
        "musandam": ["khasab", "dibba", "madha"],


        // Kuwait
        "ahmadi": ["mangaf", "sulaibikhat", "al_zour", "raqai", "wafra", "abdali", "shalim",],
        "jahra": ["mangaf", "al_jahra", "abdali"],
        "capital": ["kuwait_city", "salmiya", "hawalli", "farwaniya", "al_jahra"],
        "farwaniya": ["farwaniya", "al_jahra", "al_zour",],
        "hawalli": ["hawalli", "salmiya", "al_jahra",],
        "mubarak_al_kabeer": ["al_jahra",],


        // Bahrain
        "capital_governorate": ["manama", "seef", "bilad_al_qadeem", "ain_adari"],
        "muharraq_governorate": ["muharraq", "busaiteen", "hidd",],
        "southern_governorate": ["riffa", "janabiyah", "saar", "zallaq", "jasra", "karzakan", "duraz"],
        "northern_governorate": ["hamad_town", "budaiya", "jidhafs", "sitra", "malikiya", "maameer", "buri", "diraz"],

        // Qatar
        // "doha_region": ["doha", "al_rayyan", "mesaieed", "umm_said", "al_wukair"],
        // "al_shamal_region": ["madinat_ash_shamal", "al_khor", "fuwayrit", "al_thakhira"],
        // "al_shahaniya_region": ["al_shahaniya", "dukhan"],
        // "ras_laffan_region": ["ras_laffan"],
        // "umm_bab_region": ["umm_bab"],
        "doha_municipality": ["doha", "al_dafna", "west_bay", "al_sadd", "al_thumama", "lulu", "al_waab"],
        "al_rayyan_municipality": ["al_rayyan", "um_abrakh", "al_sheraq", "laban", "muaither"],
        "al_wakrah_municipality": ["al_wakrah", "al_wakif", "al_sheehaniya", "uqdat_ali"],
        "umm_said_municipality": ["umm_said", "al_nasr", "um_salal"],
        "al_khor_municipality": ["al_khor", "al_thakhira", "al_ghariya", "al_waab", "um_salal"],


        // UAE
        "abu_dhabi_emirate": ["abu_dhabi", "al_ain", "hili", "al_qua"],
        "dubai_emirate": ["dubai",],
        "sharjah_emirate": ["sharjah", "kalba", "khor_fakkan", "dibba_al_fujairah"],
        "ajman_emirate": ["ajman"],
        "ras_al_khaimah_emirate": ["ras_al_khaimah", "al_jazirah_al_hamra", "masafi"],
        "fujairah_emirate": ["fujairah"],
        "umm_al_quwain_emirate": ["umm_al_quwain"],
    };
    const cities = [];

    if (includeState.length > 0) {
        if (state_city_map[includeState]) {
            state_city_map[includeState].forEach(city_code => {
                cities.push({
                    value: city_code,
                    label: t(`general.city.${city_code}`)
                });
            });
        }
    } else {
        // Optionally: If no state is selected, you can default to some logic or return an empty array.
        for (const state in state_city_map) {
            state_city_map[state].forEach(city_code => {
                cities.push({
                    value: city_code,
                    label: t(`general.city.${city_code}`)
                });
            });
        }
    }

    return cities;
};

export const useGetRoleBasedSkillsHook = () => {
    const [roleSkills, setRoleSkills] = useState([]);
    const { t } = useTranslation();
    const currentLanguage = i18next.language;

    useEffect(() => {
        const fetchRoleSkills = async () => {
            try {
                console.log("Fetching role-based skills...");
                const response = await axios.get(`${process.env.REACT_APP_API_URL}job/getRoleBasedSkills`);
                const data = response.data || [];
                console.log("Fetched role-based skills:", data);

                const processedRoleSkills = Array.isArray(data) ? data.map(role => {
                    if (!role) return null;
                    
                    const processedRole = {
                        id: role.id || Math.random().toString(),
                        value: role.value || '',
                        label: currentLanguage === "en" ? (role.label_en || '') : (role.label_ar || ''),
                        skills: currentLanguage === "en" ? (role.skills_en || []) : (role.skills_ar || [])
                    };
                    
                     return processedRole;
                }).filter(Boolean) : [];
                
                 setRoleSkills(processedRoleSkills);
            } catch (error) {
                console.error('Error fetching role-based skills:', error);
                setRoleSkills([]); // Ensure we set an empty array on error
            }
        };

        fetchRoleSkills();
    }, [currentLanguage]);

    return roleSkills;
};
